import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Modal } from 'antd'
import { useRef } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { segmentsQueries } from '@/api/segments/segmentsQueries'
import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import AtomRadioGroup from '@/components/Atoms/AtomRadioGroup/AtomRadioGroup'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import { useCreateSegmentMutation } from '@/hooks/segments/useCreateSegmentMutation'
import { useUpdateSegmentMutation } from '@/hooks/segments/useUpdateSegmentMutation'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormInputControlType,
  FormInputRegisterType,
  SelectFormControlType,
} from '@/types'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import {
  buildBaseSegmentOptions,
  buildSegmentFormDefaults,
  PRICING_MODE_OPTIONS,
  segmentFormSchema,
  type SegmentFormType,
  type SegmentModalMode,
  toCreateSegmentRequest,
  toUpdateSegmentRequest,
} from './SegmentCreationModal.config'
import cn from './SegmentCreationModal.module.scss'
import SegmentPricingDefaults from './SegmentPricingDefaults/SegmentPricingDefaults'

type SegmentCreationModalProps = {
  mode: SegmentModalMode
  open: boolean
  onClose: () => void
}

function SegmentCreationModal({
  mode,
  open,
  onClose,
}: SegmentCreationModalProps) {
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const [modal, contextHolder] = Modal.useModal()
  const previewQuery = useQuery(segmentsQueries.preview())
  const createSegment = useCreateSegmentMutation()
  const updateSegment = useUpdateSegmentMutation()
  const submittingRef = useRef(false)
  const isEdit = mode.type === 'edit'

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SegmentFormType>({
    resolver: zodResolver(segmentFormSchema),
    defaultValues: buildSegmentFormDefaults(mode),
    mode: 'onTouched',
    shouldFocusError: false,
  })
  const pricingMode = useWatch({ control, name: 'pricingMode' })

  const mutationCallbacks = (successMessage: string) => ({
    onSuccess: () => {
      toast.success(successMessage)
      onClose()
    },
    onError: (error: unknown) => handleApiError(error, modal),
    onSettled: () => {
      submittingRef.current = false
    },
  })

  const handleSave = (values: SegmentFormType) => {
    if (submittingRef.current) return
    submittingRef.current = true

    if (mode.type === 'edit') {
      updateSegment.mutate(
        {
          id: mode.segment.id,
          payload: toUpdateSegmentRequest(values, mode.segment),
        },
        mutationCallbacks('Сегмент обновлён')
      )
      return
    }

    createSegment.mutate(
      toCreateSegmentRequest(values),
      mutationCallbacks('Сегмент создан')
    )
  }

  return (
    <>
      {contextHolder}
      <AtomModalWithForm
        title={isEdit ? 'Редактирование сегмента' : 'Новый сегмент'}
        submitText={isEdit ? 'Сохранить' : 'Создать'}
        open={open}
        submitting={createSegment.isPending || updateSegment.isPending}
        onCancel={onClose}
        onSubmit={(event) => {
          event.preventDefault()
          void handleSubmit(handleSave, handleFormSubmitError(isValid))()
        }}
      >
        <AtomFormInput
          layout="stacked"
          idPrefix="segment-"
          isOrganisationSelected={isOrganisationSelected}
          location="segmentCreation"
          name="description"
          title="Наименование"
          placeholder="Например: VIP"
          errors={errors}
          register={register as FormInputRegisterType}
          control={control as FormInputControlType}
          required
        />

        <div className={cn.group}>
          <span className={cn.groupLabel}>Ценообразование</span>
          <Controller
            control={control}
            name="pricingMode"
            render={({ field }) => (
              <AtomRadioGroup
                vertical
                options={PRICING_MODE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                isOrganisationSelected={isOrganisationSelected}
              />
            )}
          />
        </div>

        {pricingMode === 'base' && (
          <>
            <HoverElement>
              <AtomSelect
                layout="stacked"
                fullWidth
                isOrganisationSelected={isOrganisationSelected}
                title="Базовый сегмент"
                placeholder="Выберите сегмент"
                options={
                  previewQuery.data
                    ? buildBaseSegmentOptions(previewQuery.data)
                    : []
                }
                loading={previewQuery.isPending}
                showSearch
                required
                formName="baseSegment"
                formErrors={errors}
                formControl={control as SelectFormControlType}
                openByHover={false}
                popupClassName={
                  isOrganisationSelected ? cn.popupSelected : cn.popup
                }
              />
            </HoverElement>
            {previewQuery.isError && (
              <div className={cn.previewError}>
                <span>Не удалось загрузить сегменты.</span>
                <button type="button" onClick={() => previewQuery.refetch()}>
                  Повторить
                </button>
              </div>
            )}
            <AtomFormInput
              layout="stacked"
              idPrefix="segment-"
              isOrganisationSelected={isOrganisationSelected}
              location="segmentCreation"
              name="priceModifierPercentage"
              title="Наценка (+) или скидка (−), %"
              placeholder="0"
              errors={errors}
              register={register as FormInputRegisterType}
              control={control as FormInputControlType}
            />
          </>
        )}

        <SegmentPricingDefaults
          control={control}
          register={register}
          errors={errors}
          editingSegmentId={isEdit ? mode.segment.id : null}
          isRrcLocked={isEdit && mode.segment.isRrc}
          isOrganisationSelected={isOrganisationSelected}
        />

        {!isEdit && (
          <Controller
            control={control}
            name="isDefaultForNewClients"
            render={({ field }) => (
              <AtomCheckbox
                className={cn.checkbox}
                checked={field.value}
                onChange={field.onChange}
                isOrganisationSelected={isOrganisationSelected}
              >
                По умолчанию для новых клиентов
              </AtomCheckbox>
            )}
          />
        )}
      </AtomModalWithForm>
    </>
  )
}

export default SegmentCreationModal
